// src/lib/api.ts（完全版：characterIpName 対応 + /character が data.lists の場合も吸収）
import { getDeviceParams } from "./device";
import { API_BASE_URL, API_KEY } from "./config";
import { AppError } from "./errors";
import { getByPath, pickNumberByPaths, pickStringByPaths } from "./path";
import { storage } from "./storage";

type ApiEnvelope<T = any> = {
  code: number | string;
  message?: string;
  data?: T;
};

type CreateUserData = { id: string; secretKey: string };
type LoginData = { accessToken: string };

export type UserData = {
  userName?: string;
  description?: string;
  intimacy?: any;

  // コイン残高
  totalAmount?: number;        // 現在の利用残高（有償＋無償）
  totalAmountPaid?: number;    // 現在の利用残高（有償）
  totalAmountFree?: number;    // 現在の利用残高（無償）

  currentCharacter?: {
    characterId?: string | number;
    characterIpName?: string; // ★モデル名
    characterName?: string;
    rankName?: string;
    rankUpPoint?: number;
    nextRankUpPoint?: number;
    description?: string;
    image?: string; // バックエンドから取得した画像パス（ドメイン除外）
    iconImage?: string; // バックエンドから取得したアイコン画像パス（ドメイン除外）
  };

  currentVoice?: { id?: string | number; name?: string };
};

export type CharacterData = {
  characterId?: string | number;
  id?: string | number;

  characterName?: string;
  name?: string;

  description?: string;
  messageToUser?: string;
  reportMemoToUser?: string;

  characterIpName?: string; // ★モデル名
  image?: string; // バックエンドから取得した画像パス（ドメイン除外）
  imageUrl?: string; // バックエンドから取得した画像 URL（フルURL形式）
  iconImage?: string; // バックエンドから取得したアイコン画像パス（ドメイン除外）
  iconImageUrl?: string; // バックエンドから取得したアイコン画像 URL（フルURL形式）
  isCurrent?: boolean;
  voice?: { id?: string | number; name?: string; label?: string } | null;
};

export type VoiceData = {
  id?: string | number;
  label?: string;
  name?: string;
  displayName?: string;
};

export type ChatHistoryItem = {
  id: string;
  seq?: number;
  date?: string;
  inout: number; // 0 user, 1 AI
  text: string;
  isGood?: number;
};

export type ChatHistoryData = { histories?: ChatHistoryItem[] };

export type ChatModeValue = 0 | 1;

export type ChatModeItem = {
  mode: ChatModeValue;
  name: string;
  unlocked: boolean;
  requiredRank: number | null;
};

export type ChatModesData = {
  modes: ChatModeItem[];
};

export type ChatTalkResponseData = {
  messageType?: string;
  responseType?: string;
  ttsModelName?: string;
  ttsLength?: string;
  speechFileUrl?: string;
  chatHistoryId?: string;
  message?: string;
  coord?: string;
  messages?: {
    id?: string;
    seq?: string | number;
    date?: string;
    inout?: string | number;
    text?: string;
    emotion?: string | number;
  };
  intimacy?: any;
};

export type EventLoginData = {
  status: number;
  this_points: number;
  current_rank: string;
  current_point: number;
  next_rankup_point: number;
  msg: string;
};

export type GoogleIntegrationConnectData = {
  scopeBundle: string;
  authorizationUrl: string;
  state: string;
  expiresInSeconds: number;
  resolvedScopeBundle?: string;
  resolvedScopes?: string[];
};

export type GoogleIntegrationStatusData = {
  connectionStatus: string;
  isConnected: boolean;
  scopeBundle: string;
  scopeBundleGranted: boolean;
  missingScopes?: string[];
  email?: string;
  connectedAt?: string;
  lastError?: string | null;
};

export type GoogleIntegrationDisconnectData = {
  disconnected: boolean;
  previousStatus?: string;
  connectionStatus?: string;
};

export type ReceiptJobCreateData = {
  jobId: string;
  clientRequestId: string;
  status: "accepted";
  acceptedAt?: string;
};

export type ReceiptJobStatusData = {
  jobId: string;
  clientRequestId: string;
  status: "accepted" | "completed" | "failed";
  processedAt?: string | null;
  extractedSummary?: string | null;
  failedStage?: string | null;
  failedError?: string | null;
};

export type LineLinkCodeData = {
  link_code: string;
  expires_at: string;
  instruction: string;
};

export type LineLinkStatusData = {
  status?: string;
  isLinked?: boolean;
  notificationUserId?: string;
};

export type LineLinkDisconnectData = {
  disconnected: boolean;
  previousStatus?: string;
  status?: string;
};

export type ProfileMasterItem = {
  key: string;
  label: string;
  displayOrder: number;
  valueType?: string | null;
};

export type UserProfileItem = {
  key: string;
  label: string;
  value: string;
  displayOrder: number;
};

export type UserProfileUpdateItem = {
  key: string;
  value: string;
};

export type GroupTalkSessionRole = "facilitator" | "expert";
export type GroupTalkSpeakerType = "user" | "facilitator" | "expert";

export type GroupTalkSessionStateDto = {
  current_category?: string;
  current_focus_point?: string;
  open_questions?: string[];
  decision_status?: string;
};

export type GroupTalkParticipantDto = {
  participant_id: string;
  member_id: string;
  speaker_id: string;
  display_name?: string | null;
  role: GroupTalkSessionRole;
  stance?: string | null;
};

export type GroupTalkMessageDto = {
  message_id: string;
  participant_id?: string | null;
  member_id?: string | null;
  speaker_id: string;
  speaker_name: string;
  speaker_type: GroupTalkSpeakerType;
  phase?: string | null;
  content: string;
};

export type GroupTalkSessionResponse = {
  session_id: string;
  template_id?: string | null;
  title: string;
  description?: string | null;
  members: GroupTalkParticipantDto[];
  agreed_notes?: string[] | string | null;
  session_state?: GroupTalkSessionStateDto | null;
  status?: string;
  created_at: string;
};

export type GroupTalkTurnResponse = {
  session_id: string;
  turn_id: string;
  messages: GroupTalkMessageDto[];
  agreed_notes?: string[] | string | null;
  session_state?: GroupTalkSessionStateDto | null;
  is_satisfied?: boolean;
  created_at: string;
};

export type CreateGroupTalkSessionParams = {
  templateId: string;
  title: string;
  description: string;
  members: Array<{
    participantId: string;
    memberId: string;
    speakerId: string;
    role: GroupTalkSessionRole;
    stance?: string | null;
  }>;
};

export type CreateGroupTalkTurnParams = {
  userMessage: string;
};

// ====== コイン購入関連の型定義 ======

// 商品（コインプラン）の型定義
export type Product = {
  id: string;                 // アプリ内課金商品ID
  storeType: 1 | 2;          // 1:App Store, 2:Google Play
  productType: 1 | 2;        // 1:サブスク, 2:コイン
  productId: string;         // プロダクトID（例: jp.co.xxxx.consumable.item1）
  name: string;              // 商品名（例: "コイン100"）
  price: number;             // 商品価格
  coin: number;              // 有償コイン数
  free_coin: number;         // 無償コイン数
  description: string;       // 商品説明
  itemCode: string;          // アイテムコード
  imageFileName?: string;    // 商品画像ファイル名
  salesFrom: string;         // 販売期間（from）ISO 8601
  salesTo: string;           // 販売期間（to）ISO 8601
  isOneTime: 0 | 1;         // 0:何度でも購入可能, 1:一回限り購入可能
  isPurchased: 0 | 1;       // 0:未購入, 1:購入済
  createdAt: string;         // 作成日時
  updatedAt: string;         // 更新日時
};

// プラン一覧レスポンス
export type ProductListResponse = {
  lists: Product[];
};

// 購入リクエスト
export type ProductPurchaseRequest = {
  userId: string;
  iapProductId?: string;     // アプリ内課金商品ID（0円商品以外は必須）
  storeType: 1 | 2;          // 1:App Store, 2:Google Play
  transactionId?: string;    // トランザクションID（0円商品以外は必須）
  purchaseToken?: string;    // 購入トークン
};

// 購入レスポンス
export type ProductPurchaseResponse = {
  // レスポンスにdataフィールドなし（購入後は /user を再取得して最新残高を確認）
};

export type NewsNotificationType = "line" | "google_chat" | "email";

export type NewsJobMode = "news" | "report";
export type NewsReadingProfile = "grasp" | "organize" | "think";
export type NewsDeliveryOption = "text" | "audio" | "text_and_audio";

export type NewsJobRequest = {
  keyword: string;
  notificationType: NewsNotificationType;
  email?: string;
  redirectUrl?: string;
  mode?: NewsJobMode;
  readingProfile?: NewsReadingProfile;
  deliveryOption?: NewsDeliveryOption;
  userProfile?: string;
};

export type NewsJobResponse = {
  id: string;
  jobId: string;
  status: "accepted";
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
};

export type NewsJobStatusData = {
  id: string;
  jobId: string;
  status: "accepted" | "completed" | "failed";
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
  processedAt?: string | null;
  failedStage?: string | null;
  errorMessage?: string | null;
  mode?: NewsJobMode | null;
  readingProfile?: NewsReadingProfile | null;
  callbackMessage?: string | null;
  summary?: string | null;
};

export type AgentJobItem = {
  id: string;
  resource: string;
  jobId: string;
  status: string;
  rawStatus?: string;
  summary?: string | null;
  requestedAt: string;
  acceptedAt?: string | null;
  processedAt?: string | null;
  failedAt?: string | null;
  updatedAt: string;
  errorMessage?: string | null;
  meta?: any;
};

export type AgentJobsData = {
  items: AgentJobItem[];
};

export type GoogleSsoInvalidateResult = {
  invalidated: boolean;
  alreadyInvalidated?: boolean;
  effectiveFrom?: string | null;
};

type GoogleSsoData = {
  email: string;
  userId?: string;
  secretKey?: string;
  accessToken?: string;
  isNewUser?: boolean;
};

type AuthSession = {
  userId: string;
  secretKey?: string;
  accessToken: string;
};

// ====== 設定 ======
const API_PREFIX = "/api/v1";

// バックエンドが返す招待コード要求メッセージ。api.ts 内でのみ参照する。
// バックエンドのメッセージが変わった場合はここを更新すること。
const INVITE_CODE_REQUIRED_MESSAGE = "招待コードが必要です。" as const;

function normalizeBaseURL(v: string) {
  if (!v) return "";
  return v.endsWith("/") ? v.slice(0, -1) : v;
}
const NORMALIZED_API_BASE_URL = normalizeBaseURL(API_BASE_URL);

// ====== util ======
function safeText(t: string, limit = 2000) {
  if (!t) return "";
  return t.length > limit ? t.slice(0, limit) + "..." : t;
}

function statusToCode(status: number) {
  if (status === 400) return "BAD_REQUEST" as const;
  if (status === 401) return "UNAUTHORIZED" as const;
  if (status === 403) return "FORBIDDEN" as const;
  if (status === 404) return "NOT_FOUND" as const;
  if (status === 409) return "CONFLICT" as const;
  if (status === 503) return "MAINTENANCE" as const;
  if (status >= 500) return "SERVER" as const;
  return "UNKNOWN" as const;
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

function buildPath(pathWithoutPrefix: string) {
  if (!pathWithoutPrefix.startsWith("/")) {
    throw new AppError("Invalid API path (must start with '/').", "CONFIG", {
      endpoint: pathWithoutPrefix,
      method: "UNKNOWN",
    });
  }
  return `${API_PREFIX}${pathWithoutPrefix}`;
}

function buildEndpoint(fullPath: string) {
  if (!fullPath.startsWith("/")) {
    throw new AppError("Invalid API path (must start with '/').", "CONFIG", {
      endpoint: fullPath,
      method: "UNKNOWN",
    });
  }

  if (!NORMALIZED_API_BASE_URL) return fullPath;

  // REACT_APP_API_BASE_URL が "/api" のようなパス指定の場合
  if (NORMALIZED_API_BASE_URL.startsWith("/")) {
    // fullPath が既に "/api/..." を含んでいるなら二重付与しない
    if (fullPath === NORMALIZED_API_BASE_URL || fullPath.startsWith(`${NORMALIZED_API_BASE_URL}/`)) {
      return fullPath;
    }
    return `${NORMALIZED_API_BASE_URL}${fullPath}`;
  }

  // "https://example.com" のような絶対URLの場合
  return `${NORMALIZED_API_BASE_URL}${fullPath}`;
}

function unwrapList<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data.lists)) return data.lists as T[];
  return [];
}

function normalizeChatModeValue(value: unknown): ChatModeValue | null {
  if (value === 0 || value === 1) return value;
  if (value === "0" || value === "1") return Number(value) as ChatModeValue;
  return null;
}

function normalizeChatModeItem(value: any): ChatModeItem | null {
  const mode = normalizeChatModeValue(value?.mode);
  const name = typeof value?.name === "string" ? value.name.trim() : "";

  let unlocked: boolean | null = null;
  if (typeof value?.unlocked === "boolean") {
    unlocked = value.unlocked;
  } else if (typeof value?.unlocked === "number") {
    unlocked = value.unlocked !== 0;
  } else if (typeof value?.unlocked === "string") {
    const lowered = value.unlocked.trim().toLowerCase();
    if (lowered === "true" || lowered === "1") unlocked = true;
    if (lowered === "false" || lowered === "0") unlocked = false;
  }

  const rawRequiredRank = value?.requiredRank ?? value?.required_rank;
  let requiredRank: number | null = null;
  if (rawRequiredRank !== undefined && rawRequiredRank !== null && rawRequiredRank !== "") {
    const normalizedRequiredRank = Number(rawRequiredRank);
    if (Number.isFinite(normalizedRequiredRank)) {
      requiredRank = normalizedRequiredRank;
    }
  }

  if (mode === null || !name || unlocked === null) return null;

  return {
    mode,
    name,
    unlocked,
    requiredRank: mode === 0 ? null : requiredRank,
  };
}

function pickBooleanByPaths(obj: any, paths: string[]): boolean | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const lowered = value.trim().toLowerCase();
      if (["true", "1", "linked", "active", "connected"].includes(lowered)) return true;
      if (["false", "0", "unlinked", "inactive", "disconnected"].includes(lowered)) return false;
    }
  }

  return undefined;
}

function normalizeProfileMasterItem(data: any): ProfileMasterItem | null {
  const key = pickStringByPaths(data, ["key", "profileKey", "profile_key", "master.key"]);
  const label = pickStringByPaths(data, ["label", "name", "displayName", "display_name", "master.label"]);
  const displayOrder =
    pickNumberByPaths(data, ["displayOrder", "display_order", "master.displayOrder", "master.display_order"]) ?? 0;

  if (!key || !label) {
    return null;
  }

  return {
    key,
    label,
    displayOrder,
    valueType: pickStringByPaths(data, ["valueType", "value_type", "master.valueType", "master.value_type"]),
  };
}

function normalizeUserProfileItem(data: any): UserProfileItem | null {
  const key = pickStringByPaths(data, ["key", "profileKey", "profile_key", "master.key"]);
  const label = pickStringByPaths(data, ["label", "name", "displayName", "display_name", "master.label"]);
  const displayOrder =
    pickNumberByPaths(data, ["displayOrder", "display_order", "master.displayOrder", "master.display_order"]) ?? 0;
  const value =
    pickStringByPaths(data, ["value", "profileValue", "profile_value", "userProfile.value", "user_profile.value"]) ?? "";

  if (!key || !label) {
    return null;
  }

  return {
    key,
    label,
    value,
    displayOrder,
  };
}

function pickStringArrayByPaths(obj: any, paths: string[]): string[] | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (!Array.isArray(value)) continue;

    const normalized = value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter((item) => item.length > 0);
    return normalized;
  }

  return undefined;
}

function normalizeGoogleIntegrationStatusData(data: any): GoogleIntegrationStatusData {
  const connectionStatus =
    pickStringByPaths(data, [
      "connectionStatus",
      "connection_status",
      "google.connectionStatus",
      "google.connection_status",
    ]) ?? "";
  const scopeBundle =
    pickStringByPaths(data, ["scopeBundle", "scope_bundle", "google.scopeBundle", "google.scope_bundle"]) ?? "";
  const missingScopes = pickStringArrayByPaths(data, [
    "missingScopes",
    "missing_scopes",
    "google.missingScopes",
    "google.missing_scopes",
  ]);
  const scopeBundleGranted =
    pickBooleanByPaths(data, [
      "scopeBundleGranted",
      "scope_bundle_granted",
      "google.scopeBundleGranted",
      "google.scope_bundle_granted",
    ]) ?? false;
  const isConnected =
    pickBooleanByPaths(data, ["isConnected", "is_connected", "connected", "google.isConnected"]) ??
    connectionStatus.toLowerCase() !== "not_connected";
  const email = pickStringByPaths(data, ["email", "google.email"]);
  const connectedAt = pickStringByPaths(data, ["connectedAt", "connected_at", "google.connectedAt", "google.connected_at"]);
  const lastError = pickStringByPaths(data, ["lastError", "last_error", "google.lastError", "google.last_error"]);

  if (!scopeBundle) {
    throw new AppError("Invalid /integrations/google/status response.", "INVALID_RESPONSE");
  }

  return {
    connectionStatus,
    isConnected,
    scopeBundle,
    scopeBundleGranted,
    missingScopes,
    email,
    connectedAt,
    lastError: lastError ?? null,
  };
}

function parseJsonSafely(text?: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function normalizeGoogleIntegrationStatusConflictFallback(
  error: unknown
): GoogleIntegrationStatusData | null {
  if (!(error instanceof AppError) || error.code !== "CONFLICT") {
    return null;
  }

  const parsed = parseJsonSafely(error.meta?.responseText);
  const dataCandidate =
    parsed && typeof parsed === "object" && "data" in (parsed as Record<string, unknown>)
      ? (parsed as { data?: unknown }).data
      : parsed;

  if (dataCandidate && typeof dataCandidate === "object") {
    try {
      return normalizeGoogleIntegrationStatusData(dataCandidate);
    } catch {
      // Ignore invalid payload.
    }
  }

  return null;
}

function createGoogleIntegrationStatusNotConnectedFallback(
  scopeBundle: string,
  error: AppError
): GoogleIntegrationStatusData {
  return {
    connectionStatus: "not_connected",
    isConnected: false,
    scopeBundle,
    scopeBundleGranted: false,
    missingScopes: [],
    lastError: error.message ?? null,
  };
}

function buildGoogleIntegrationStatusPath(scopeBundle: string, wizmateUserId?: string) {
  return buildPath(
    `/integrations/google/status${buildQuery({
      wizmate_user_id: wizmateUserId,
      scopeBundle,
    })}`
  );
}

function normalizeGoogleIntegrationConnectData(data: any): GoogleIntegrationConnectData {
  const scopeBundle =
    pickStringByPaths(data, ["scopeBundle", "scope_bundle", "google.scopeBundle", "google.scope_bundle"]) ?? "";
  const authorizationUrl =
    pickStringByPaths(data, [
      "authorizationUrl",
      "authorization_url",
      "google.authorizationUrl",
      "google.authorization_url",
    ]) ?? "";
  const state = pickStringByPaths(data, ["state", "google.state"]) ?? "";
  const expiresInSeconds =
    pickNumberByPaths(data, ["expiresInSeconds", "expires_in_seconds", "google.expiresInSeconds", "google.expires_in_seconds"]) ??
    0;
  const resolvedScopeBundle = pickStringByPaths(data, [
    "resolvedScopeBundle",
    "resolved_scope_bundle",
    "google.resolvedScopeBundle",
    "google.resolved_scope_bundle",
  ]);
  const resolvedScopes = pickStringArrayByPaths(data, [
    "resolvedScopes",
    "resolved_scopes",
    "google.resolvedScopes",
    "google.resolved_scopes",
  ]);

  if (!scopeBundle || !authorizationUrl) {
    throw new AppError("Invalid /integrations/google/connect response.", "INVALID_RESPONSE");
  }

  return {
    scopeBundle,
    authorizationUrl,
    state,
    expiresInSeconds,
    resolvedScopeBundle,
    resolvedScopes,
  };
}

function isDisconnectedStatusValue(value?: string): boolean {
  if (!value) return false;

  return ["revoked", "disconnected", "inactive", "unlinked"].includes(value.trim().toLowerCase());
}

function isFormDataBody(body: RequestInit["body"]) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function buildRequestHeaders(options: RequestInit): Record<string, string> {
  const deviceParams = getDeviceParams();

  return {
    ...(isFormDataBody(options.body) ? {} : { "Content-Type": "application/json" }),
    Accept: "application/json",
    ...(API_KEY ? { "api-key": API_KEY } : {}),
    "X-Requested-With": "XMLHttpRequest",
    "X-Terminal-Identification-Number": deviceParams.terminalIdentificationNumber,
    ...(options.headers as any),
  };
}

function getResponseMeta(res: Response) {
  return {
    responseUrl: safeText(res.url ?? ""),
    responseContentType: safeText(res.headers?.get("content-type") ?? ""),
    location: safeText(res.headers?.get("location") ?? ""),
    redirected: res.redirected,
  };
}

function isLikelyHtmlResponse(res: Response, text: string): boolean {
  const contentType = (res.headers?.get("content-type") ?? "").toLowerCase();
  if (contentType.includes("text/html")) return true;

  const normalized = text.trimStart().toLowerCase();
  return normalized.startsWith("<!doctype html") || normalized.startsWith("<html");
}

function uniqStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

function buildApiPrefixFallbackEndpoints(endpoint: string): string[] {
  const fallbacks: string[] = [];
  const backendFallbackHosts = ["https://api.wizmate.jp", "https://origin-api.wizmate.jp"] as const;

  if (endpoint.startsWith("/api/")) {
    fallbacks.push(endpoint.slice(4));

    if (typeof window !== "undefined" && window.location?.origin) {
      fallbacks.push(`${window.location.origin}${endpoint}`);
      fallbacks.push(`${window.location.origin}${endpoint.slice(4)}`);
    }

    // canonical host fallback (alias domain or edge behavior mismatch)
    fallbacks.push(`https://wizmate.jp${endpoint}`);
    fallbacks.push(`https://wizmate.jp${endpoint.slice(4)}`);

    for (const host of backendFallbackHosts) {
      fallbacks.push(`${host}${endpoint}`);
    }

    return uniqStrings(fallbacks);
  }

  if (endpoint.startsWith("/v1/")) {
    fallbacks.push(`/api${endpoint}`);

    if (typeof window !== "undefined" && window.location?.origin) {
      fallbacks.push(`${window.location.origin}/api${endpoint}`);
    }

    fallbacks.push(`https://wizmate.jp/api${endpoint}`);

    for (const host of backendFallbackHosts) {
      fallbacks.push(`${host}/api${endpoint}`);
    }

    return uniqStrings(fallbacks);
  }

  if (!endpoint.startsWith("http://") && !endpoint.startsWith("https://")) {
    return [];
  }

  try {
    const parsed = new URL(endpoint);
    if (!parsed.pathname.startsWith("/api/") && !parsed.pathname.startsWith("/v1/")) {
      return [];
    }

    if (parsed.pathname.startsWith("/api/")) {
      const withoutApiPrefix = new URL(parsed.toString());
      withoutApiPrefix.pathname = withoutApiPrefix.pathname.slice(4);
      fallbacks.push(withoutApiPrefix.toString());

      const canonicalApi = new URL(parsed.toString());
      canonicalApi.hostname = "wizmate.jp";
      fallbacks.push(canonicalApi.toString());

      const canonicalV1 = new URL(parsed.toString());
      canonicalV1.hostname = "wizmate.jp";
      canonicalV1.pathname = canonicalV1.pathname.slice(4);
      fallbacks.push(canonicalV1.toString());

      for (const host of backendFallbackHosts) {
        fallbacks.push(`${host}${parsed.pathname}${parsed.search}`);
      }
    }

    if (parsed.pathname.startsWith("/v1/")) {
      const withApiPrefix = new URL(parsed.toString());
      withApiPrefix.pathname = `/api${withApiPrefix.pathname}`;
      fallbacks.push(withApiPrefix.toString());

      const canonicalApi = new URL(withApiPrefix.toString());
      canonicalApi.hostname = "wizmate.jp";
      fallbacks.push(canonicalApi.toString());

      for (const host of backendFallbackHosts) {
        fallbacks.push(`${host}/api${parsed.pathname}${parsed.search}`);
      }
    }

    return uniqStrings(fallbacks);
  } catch {
    return [];
  }
}

async function requestJson<T>(fullPath: string, options: RequestInit): Promise<T> {
  const method = (options.method ?? "GET").toString();
  const endpoint = buildEndpoint(fullPath);
  const fallbackAttempts: Array<{
    endpoint: string;
    status?: number;
    contentType?: string;
    result: "success" | "non_json" | "http_error" | "network_error";
  }> = [];

  let res: Response;
  let text = "";

  try {
    const headers = buildRequestHeaders(options);

    res = await fetch(endpoint, { ...options, headers });
    text = await res.text().catch(() => "");

    if (!res.ok) {
      throw new AppError(`API request failed: ${res.status} ${res.statusText}`, statusToCode(res.status), {
        endpoint,
        method,
        status: res.status,
        responseText: safeText(text),
        ...getResponseMeta(res),
      });
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      const fallbackEndpoints = buildApiPrefixFallbackEndpoints(endpoint);
      if (fallbackEndpoints.length > 0 && isLikelyHtmlResponse(res, text)) {
        for (const fallbackEndpoint of fallbackEndpoints) {
          let fallbackRes: Response;
          let fallbackText = "";

          try {
            fallbackRes = await fetch(fallbackEndpoint, { ...options, headers });
            fallbackText = await fallbackRes.text().catch(() => "");

            if (!fallbackRes.ok) {
              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "http_error",
              });
              continue;
            }

            try {
              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "success",
              });
              return JSON.parse(fallbackText) as T;
            } catch {
              if (isLikelyHtmlResponse(fallbackRes, fallbackText)) {
                fallbackAttempts.push({
                  endpoint: fallbackEndpoint,
                  status: fallbackRes.status,
                  contentType: fallbackRes.headers?.get("content-type") ?? "",
                  result: "non_json",
                });
                continue;
              }

              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "non_json",
              });
            }
          } catch (fallbackError) {
            fallbackAttempts.push({
              endpoint: fallbackEndpoint,
              result: "network_error",
            });
          }
        }

        const fallback403 = fallbackAttempts.find((attempt) => attempt.result === "http_error" && attempt.status === 403);
        if (fallback403) {
          throw new AppError("API request failed: 403 Forbidden", "FORBIDDEN", {
            endpoint: fallback403.endpoint,
            method,
            status: 403,
            responseText: safeText(text),
            fallbackFromEndpoint: endpoint,
            fallbackAttempts,
          });
        }

        const fallback401 = fallbackAttempts.find((attempt) => attempt.result === "http_error" && attempt.status === 401);
        if (fallback401) {
          throw new AppError("API request failed: 401 Unauthorized", "UNAUTHORIZED", {
            endpoint: fallback401.endpoint,
            method,
            status: 401,
            responseText: safeText(text),
            fallbackFromEndpoint: endpoint,
            fallbackAttempts,
          });
        }
      }

      console.error("[api] non-JSON response", {
        endpoint,
        method,
        status: res.status,
        text: safeText(text),
        fallbackAttempts,
      });
      throw new AppError("API returned non-JSON response.", "INVALID_RESPONSE", {
        endpoint,
        method,
        status: res.status,
        responseText: safeText(text),
        fallbackAttempts,
        ...getResponseMeta(res),
      });
    }
  } catch (e: any) {
    if (e instanceof AppError) throw e;
    throw new AppError("Network error.", "NETWORK", {
      endpoint,
      method,
      causeName: e?.name,
      responseText: safeText(String(e?.message ?? e ?? "")),
    });
  }
}

async function requestEnvelopeData<T>(fullPath: string, options: RequestInit): Promise<T> {
  const env = await requestJson<ApiEnvelope<T>>(fullPath, options);
  if (!env || typeof env !== "object" || env.data === undefined) {
    console.error("[api] missing envelope data", { endpoint: buildEndpoint(fullPath), method: options.method ?? "GET", env });
    throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(fullPath),
      method: (options.method ?? "GET").toString(),
      responseText: safeText(JSON.stringify(env)),
    });
  }
  return env.data as T;
}

async function requestEnvelopeDataMultipart<T>(fullPath: string, options: RequestInit): Promise<T> {
  const env = await requestJson<ApiEnvelope<T>>(fullPath, options);
  if (!env || typeof env !== "object" || env.data === undefined) {
    throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(fullPath),
      method: (options.method ?? "GET").toString(),
      responseText: safeText(JSON.stringify(env)),
    });
  }
  return env.data as T;
}

let authRefreshPromise: Promise<AuthSession> | null = null;

async function runPostLoginInitialization(params: {
  userId: string;
  accessToken: string;
}): Promise<void> {
  const deviceParams = getDeviceParams();
  const qs = buildQuery({
    userId: params.userId,
    terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
  });
  const path = buildPath(`/event/login${qs}`);

  try {
    await requestEnvelopeData<EventLoginData>(path, {
      method: "GET",
      headers: { Authorization: `Bearer ${params.accessToken}` },
    });
  } catch (e) {
    if (e instanceof AppError && (e.code === "UNAUTHORIZED" || e.code === "FORBIDDEN")) {
      throw e;
    }
  }
}

async function refreshStoredAuthSession(params: {
  userId: string;
  secretKey: string;
}): Promise<AuthSession> {
  if (!authRefreshPromise) {
    authRefreshPromise = (async () => {
      const loggedIn = await api.login({
        userId: params.userId,
        secretKey: params.secretKey,
      });
      const accessToken = loggedIn.accessToken;

      storage.setAuth({ accessToken });
      await runPostLoginInitialization({
        userId: params.userId,
        accessToken,
      });

      return {
        userId: params.userId,
        secretKey: params.secretKey,
        accessToken,
      };
    })().finally(() => {
      authRefreshPromise = null;
    });
  }

  return await authRefreshPromise;
}

async function withAuthRetry<T>(fn: (accessToken: string) => Promise<T>): Promise<T> {
  const auth = await api.ensureAuth();
  try {
    return await fn(auth.accessToken);
  } catch (e: any) {
    if (e instanceof AppError && e.code === "UNAUTHORIZED") {
      storage.setAuth({ accessToken: undefined });
      // secretKey がない SSO ユーザーはトークン再取得不可 → 再サインインが必要
      if (!storage.getAuth().secretKey) {
        throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
      }
      const refreshed = await api.ensureAuth();
      return await fn(refreshed.accessToken);
    }
    throw e;
  }
}

// ====== API ======
export const api = {
  async createUser(): Promise<{ userId: string; secretKey: string }> {
    const deviceParams = getDeviceParams();

    const path = buildPath("/user");
    const requestCreateUser = async (includeDeviceParams: boolean) => {
      return await requestEnvelopeData<CreateUserData>(path, {
        method: "POST",
        ...(includeDeviceParams ? { body: JSON.stringify(deviceParams) } : {}),
      });
    };

    let data: CreateUserData;
    try {
      // 現行バックエンド: /user は device params を要求
      data = await requestCreateUser(true);
    } catch (error) {
      // 互換対応: body なし実装のバックエンド向け
      if (!(error instanceof AppError) || error.code !== "BAD_REQUEST") {
        throw error;
      }
      data = await requestCreateUser(false);
    }

    const userId = data.id;
    const secretKey = data.secretKey;

    if (!userId || !secretKey) {
      throw new AppError("Invalid /user response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify({ id: data.id })),
      });
    }

    return { userId, secretKey };
  },

  async login(params: { userId: string; secretKey: string }): Promise<{ accessToken: string }> {
    const path = buildPath("/login");
    const data = await requestEnvelopeData<LoginData>(path, {
      method: "POST",
      body: JSON.stringify(params),
    });

    if (!data.accessToken) {
      throw new AppError("Invalid /login response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify({})),
      });
    }

    return { accessToken: data.accessToken };
  },

  async ensureAuth(): Promise<AuthSession> {
    const current = storage.getAuth();
    let { userId, secretKey, accessToken } = current;

    if (!userId) {
      // userId がない場合のみ新規ユーザー作成（非 SSO フロー）
      const created = await this.createUser();
      userId = created.userId;
      secretKey = created.secretKey;
      storage.setAuth({ userId, secretKey });
    }

    if (!accessToken) {
      if (!secretKey) {
        // SSO ユーザーの accessToken 期限切れ: Google 再サインインが必要
        throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
      }
      const refreshed = await refreshStoredAuthSession({ userId, secretKey });
      accessToken = refreshed.accessToken;
    }

    return { userId, secretKey, accessToken };
  },

  async getUser(): Promise<UserData> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const qs = buildQuery({
      userId,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/user${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<UserData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getProfileMaster(): Promise<ProfileMasterItem[]> {
    const path = buildPath("/profile/master");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      return unwrapList<any>(data)
        .map((item) => normalizeProfileMasterItem(item))
        .filter((item): item is ProfileMasterItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async getUserProfile(): Promise<UserProfileItem[]> {
    const path = buildPath("/profile");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      return unwrapList<any>(data)
        .map((item) => normalizeUserProfileItem(item))
        .filter((item): item is UserProfileItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async updateUserProfile(params: { profiles: UserProfileUpdateItem[] }): Promise<UserProfileItem[]> {
    const path = buildPath("/profile");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profiles: params.profiles.map((item) => ({
            key: item.key,
            value: item.value,
          })),
        }),
      });

      return unwrapList<any>(data)
        .map((item) => normalizeUserProfileItem(item))
        .filter((item): item is UserProfileItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async eventLogin(params?: { userId?: string; terminalIdentificationNumber?: string }): Promise<EventLoginData> {
    const deviceParams = getDeviceParams();
    const auth = await this.ensureAuth();

    const userId = params?.userId ?? auth.userId;
    const terminalIdentificationNumber =
      params?.terminalIdentificationNumber ?? deviceParams.terminalIdentificationNumber;

    const qs = buildQuery({ userId, terminalIdentificationNumber });
    const path = buildPath(`/event/login${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<EventLoginData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async createLineLinkCode(): Promise<LineLinkCodeData> {
    const path = buildPath("/line/link-code");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<LineLinkCodeData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!data?.link_code || !data?.expires_at) {
        throw new AppError("Invalid /line/link-code response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return {
        link_code: data.link_code,
        expires_at: data.expires_at,
        instruction:
          data.instruction || `LINE公式アカウントに「連携 ${data.link_code}」と送信してください。`,
      };
    });
  },

  async getLineLinkStatus(): Promise<LineLinkStatusData> {
    const path = buildPath("/line/status");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const status = pickStringByPaths(data, [
        "status",
        "lineStatus",
        "line_link_status",
        "connectionStatus",
        "connection_status",
        "line.status",
      ]);
      const isLinked = pickBooleanByPaths(data, [
        "isLinked",
        "is_linked",
        "lineLinked",
        "line_linked",
        "connected",
        "line.isLinked",
      ]);
      const notificationUserId = pickStringByPaths(data, [
        "notificationUserId",
        "notification_user_id",
        "lineUserId",
        "line_user_id",
        "line.notificationUserId",
        "line.userId",
      ]);

      if (!status && isLinked === undefined && !notificationUserId) {
        throw new AppError("Invalid /line/status response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { status, isLinked, notificationUserId };
    });
  },

  async disconnectLineLink(): Promise<LineLinkDisconnectData> {
    const path = buildPath("/line/link");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<LineLinkDisconnectData>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!data || typeof data.disconnected !== "boolean") {
        throw new AppError("Invalid /line/link response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "DELETE",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async updateUserSelection(params: { characterId?: string; voiceId?: string }): Promise<void> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    if (!params.characterId && !params.voiceId) {
      throw new AppError("updateUserSelection requires characterId or voiceId.", "BAD_REQUEST", {
        endpoint: buildEndpoint(buildPath(`/user/${encodeURIComponent(userId)}`)),
        method: "POST",
      });
    }

    const body: Record<string, any> = {
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    };
    if (params.characterId) body.characterId = params.characterId;
    if (params.voiceId) body.voiceId = params.voiceId;

    const path = buildPath(`/user/${encodeURIComponent(userId)}`);

    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
    });
  },

  async getCharacterList(): Promise<CharacterData[]> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const qs = buildQuery({
      userId,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/character${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (Array.isArray(data)) return data as CharacterData[];
      if (data && Array.isArray(data.characters)) return data.characters as CharacterData[];
      return unwrapList<CharacterData>(data);
    });
  },

  async getVoiceList(): Promise<VoiceData[]> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const qs = buildQuery({
      userId,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/voice${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return unwrapList<VoiceData>(data);
    });
  },

  async getChatHistory(params?: { limit?: number }): Promise<ChatHistoryItem[]> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const fetchHistory = async (
      accessToken: string,
      includeTerminalQuery: boolean
    ): Promise<ChatHistoryItem[]> => {
      const qs = buildQuery({
        userId,
        terminalIdentificationNumber: includeTerminalQuery
          ? deviceParams.terminalIdentificationNumber
          : undefined,
        limit: params?.limit ?? 30,
      });

      const path = buildPath(`/chat/history${qs}`);
      const data = await requestEnvelopeData<ChatHistoryData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return data?.histories ?? [];
    };

    return await withAuthRetry(async (accessToken) => {
      try {
        const histories = await fetchHistory(accessToken, true);
        if (histories.length > 0) return histories;
      } catch (error) {
        if (
          !(error instanceof AppError) ||
          (error.code !== "UNAUTHORIZED" && error.code !== "BAD_REQUEST" && error.code !== "NOT_FOUND")
        ) {
          throw error;
        }
      }

      try {
        return await fetchHistory(accessToken, false);
      } catch (error) {
        if (
          error instanceof AppError &&
          (error.code === "BAD_REQUEST" || error.code === "NOT_FOUND")
        ) {
          return [];
        }
        throw error;
      }
    });
  },

  async getChatModes(params?: { characterId?: string }): Promise<ChatModesData> {
    const { userId } = await this.ensureAuth();
    const qs = buildQuery({
      userId,
      characterId: params?.characterId,
    });
    const path = buildPath(`/chat/modes${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const modesSource = Array.isArray(data?.modes) ? data.modes : [];
      const modes = modesSource
        .map((mode: any) => normalizeChatModeItem(mode))
        .filter((mode: ChatModeItem | null): mode is ChatModeItem => mode !== null)
        .sort((left: ChatModeItem, right: ChatModeItem) => left.mode - right.mode);

      if (modes.length === 0) {
        throw new AppError("Invalid /chat/modes response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { modes };
    });
  },

  async chatTalk(params: {
    text: string;
    speechType?: number;
    chatMode?: ChatModeValue;
  }): Promise<ChatTalkResponseData> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const body = {
      userId,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
      text: params.text,
      speechType: params.speechType ?? 8,
      chatMode: params.chatMode ?? 0,
    };

    const path = buildPath("/chat/talk");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ChatTalkResponseData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
    });
  },

  async chatReceipt(params: { file: File }): Promise<any> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    const form = new FormData();
    form.append("userId", userId);
    form.append("terminalIdentificationNumber", deviceParams.terminalIdentificationNumber);
    form.append("file", params.file, params.file.name);

    const path = buildPath("/chat/receipt");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeDataMultipart<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
    });
  },

  async startGoogleIntegration(params: {
    scopeBundle: string;
    redirectUrl: string;
  }): Promise<GoogleIntegrationConnectData> {
    const path = buildPath("/integrations/google/connect");
    const payload = {
      scopeBundle: params.scopeBundle,
      redirectUrl: params.redirectUrl,
    };

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload),
      });
      return normalizeGoogleIntegrationConnectData(data);
    });
  },

  async getGoogleIntegrationStatus(scopeBundle: string): Promise<GoogleIntegrationStatusData> {
    const wizmateUserId = storage.getAuth().userId?.trim();
    const primaryPath = buildGoogleIntegrationStatusPath(scopeBundle, wizmateUserId);
    const fallbackPath = buildGoogleIntegrationStatusPath(scopeBundle);

    return await withAuthRetry(async (accessToken) => {
      const requestStatus = async (path: string) => {
        const data = await requestEnvelopeData<any>(path, {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        return normalizeGoogleIntegrationStatusData(data);
      };

      try {
        return await requestStatus(primaryPath);
      } catch (primaryError) {
        const primaryConflictFallback = normalizeGoogleIntegrationStatusConflictFallback(primaryError);

        const canRetryWithoutUserScope =
          !primaryConflictFallback &&
          !!wizmateUserId &&
          primaryPath !== fallbackPath &&
          primaryError instanceof AppError &&
          (primaryError.code === "BAD_REQUEST" ||
            primaryError.code === "NOT_FOUND" ||
            primaryError.code === "CONFLICT");

        if (canRetryWithoutUserScope) {
          try {
            return await requestStatus(fallbackPath);
          } catch (fallbackError) {
            const retryConflictFallback = normalizeGoogleIntegrationStatusConflictFallback(fallbackError);
            if (retryConflictFallback) {
              return retryConflictFallback;
            }
            if (fallbackError instanceof AppError && fallbackError.code === "CONFLICT") {
              return createGoogleIntegrationStatusNotConnectedFallback(scopeBundle, fallbackError);
            }
            throw fallbackError;
          }
        }

        if (primaryConflictFallback) {
          return primaryConflictFallback;
        }
        if (primaryError instanceof AppError && primaryError.code === "CONFLICT") {
          return createGoogleIntegrationStatusNotConnectedFallback(scopeBundle, primaryError);
        }
        if (primaryError instanceof AppError && primaryError.code === "UNKNOWN") {
          return createGoogleIntegrationStatusNotConnectedFallback(scopeBundle, primaryError);
        }

        throw primaryError;
      }
    });
  },

  async getGoogleIntegrationStatusByUserScope(params: {
    wizmateUserId: string;
    scopeBundle: string;
  }): Promise<GoogleIntegrationStatusData> {
    const path = buildPath(
      `/integrations/google/status${buildQuery({
        wizmate_user_id: params.wizmateUserId,
        scopeBundle: params.scopeBundle,
      })}`
    );

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeGoogleIntegrationStatusData(data);
    });
  },

  async disconnectGoogleIntegration(): Promise<GoogleIntegrationDisconnectData> {
    const path = buildPath("/integrations/google/connect");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const previousStatus = pickStringByPaths(data, [
        "previousStatus",
        "previous_status",
        "google.previousStatus",
        "google.previous_status",
      ]);
      const connectionStatus = pickStringByPaths(data, [
        "connectionStatus",
        "connection_status",
        "google.connectionStatus",
        "google.connection_status",
      ]);
      const disconnected =
        pickBooleanByPaths(data, ["disconnected", "isDisconnected", "google.disconnected"]) ??
        (isDisconnectedStatusValue(connectionStatus) ? true : undefined);

      if (disconnected === undefined) {
        throw new AppError("Invalid /integrations/google/connect response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "DELETE",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return {
        disconnected,
        previousStatus,
        connectionStatus,
      };
    });
  },

  async createReceiptJob(params: {
    file: File;
    conversationId?: string;
    messageId?: string;
  }): Promise<ReceiptJobCreateData> {
    const form = new FormData();
    form.append("file", params.file, params.file.name);

    if (params.conversationId) {
      form.append("conversationId", params.conversationId);
    }

    if (params.messageId) {
      form.append("messageId", params.messageId);
    }

    const path = buildPath("/receipt/jobs");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeDataMultipart<ReceiptJobCreateData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
    });
  },

  async getReceiptJob(jobId: string): Promise<ReceiptJobStatusData> {
    const path = buildPath(`/receipt/jobs/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ReceiptJobStatusData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async createGroupTalkSession(params: CreateGroupTalkSessionParams): Promise<GroupTalkSessionResponse> {
    const path = buildPath("/group-talk/sessions");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkSessionResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template_id: params.templateId,
          title: params.title,
          description: params.description,
          members: params.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            role: member.role,
            ...(typeof member.stance === "string" || member.stance === null ? { stance: member.stance } : {}),
          })),
        }),
      });

      if (
        !data ||
        typeof data.session_id !== "string" ||
        !Array.isArray(data.members) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async createGroupTalkTurn(sessionId: string, params: CreateGroupTalkTurnParams): Promise<GroupTalkTurnResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/turns`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkTurnResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_message: params.userMessage,
        }),
      });

      if (
        !data ||
        typeof data.session_id !== "string" ||
        typeof data.turn_id !== "string" ||
        !Array.isArray(data.messages) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/turns response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  /**
   * 商品一覧取得（コイン購入プラン）
   * @param storeType 1:App Store, 2:Google Play
   */
  async getProductList(storeType: 1 | 2): Promise<Product[]> {
    const { userId } = await this.ensureAuth();

    const qs = buildQuery({
      storeType: storeType.toString(),
      userId,
    });

    const path = buildPath(`/product/list${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<ProductListResponse>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const lists = (data.lists || []).map((item: any) => ({
        ...item,
        productId: item.productId ?? item.iapProductId ?? item.iap_product_id ?? item.id ?? "",
        price: Number(item.price ?? 0),
        coin: Number(item.coin ?? item.paidCoin ?? 0),
        free_coin: Number(item.freeCoin ?? item.free_coin ?? 0),
        isOneTime: Number(item.isOneTime ?? item.is_one_time ?? 0) as 0 | 1,
        isPurchased: Number(item.isPurchased ?? item.isPurchase ?? item.is_purchase ?? 0) as 0 | 1,
      }));

      return lists;
    });
  },

  /**
   * 商品購入（コイン購入）
   * @param params 購入パラメータ
   */
  async purchaseProduct(params: {
    productId: string;
    storeType: 1 | 2;
    transactionId?: string;
    purchaseToken?: string;
  }): Promise<void> {
    const { userId } = await this.ensureAuth();

    const body: ProductPurchaseRequest = {
      userId,
      iapProductId: params.productId,
      storeType: params.storeType,
      transactionId: params.transactionId,
      purchaseToken: params.purchaseToken,
    };

    const path = buildPath("/product/purchase");

    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<ProductPurchaseResponse>(path, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    });
  },

  async createNewsJob(params: NewsJobRequest): Promise<NewsJobResponse> {
    const buildForm = (includeAdditionalFields: boolean) => {
      const form = new URLSearchParams();
      form.set("keyword", params.keyword);
      form.set("notificationType", params.notificationType);

      if (params.email) {
        form.set("email", params.email);
      }

      if (params.redirectUrl) {
        form.set("redirectUrl", params.redirectUrl);
      }

      if (includeAdditionalFields) {
        if (params.mode) {
          form.set("mode", params.mode);
        }

        if (params.readingProfile) {
          form.set("readingProfile", params.readingProfile);
        }

        if (params.deliveryOption) {
          form.set("deliveryOption", params.deliveryOption);
        }

        if (params.userProfile) {
          form.set("userProfile[profile_text]", params.userProfile);
        }
      }

      return form;
    };

    const logNewsJobPayload = (
      form: URLSearchParams,
      attempt: 1 | 2,
      phase: "primary" | "fallback",
      when: "prepared" | "sending"
    ) => {
      if (process.env.NODE_ENV !== "development") return;
      const entries = Object.fromEntries(form.entries());
      const safe = { ...entries, email: entries.email ? "[REDACTED]" : undefined };
      console.log(`[api.createNewsJob] ${attempt}回目 ${phase} ${when} payload:`, safe);
    };

    const path = buildPath("/news/jobs");

    return await withAuthRetry(async (accessToken) => {
      const primaryForm = buildForm(true);
      const fallbackForm = buildForm(false);

      logNewsJobPayload(primaryForm, 1, "primary", "prepared");
      logNewsJobPayload(fallbackForm, 2, "fallback", "prepared");

      try {
        logNewsJobPayload(primaryForm, 1, "primary", "sending");

        const response = await requestEnvelopeData<NewsJobResponse>(path, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: primaryForm,
        });

        return response;
      } catch (error) {
        if (!(error instanceof AppError) || error.code !== "BAD_REQUEST") {
          throw error;
        }

        logNewsJobPayload(fallbackForm, 2, "fallback", "sending");

        return await requestEnvelopeData<NewsJobResponse>(path, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: fallbackForm,
        });
      }
    });
  },

  async getNewsJob(jobId: string): Promise<NewsJobStatusData> {
    const path = buildPath(`/news/jobs/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<NewsJobStatusData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getAgentJobs(params?: { resource?: "receipt" | "news"; status?: string; limit?: number }): Promise<AgentJobsData> {
    const qs = buildQuery({
      resource: params?.resource,
      status: params?.status,
      limit: params?.limit,
    });
    const path = buildPath(`/agent/jobs${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<AgentJobsData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async googleSsoLogin(params: { idToken: string; invite_code?: string }): Promise<{
    email: string;
    userId?: string;
    secretKey?: string;
    accessToken?: string;
    isNewUser?: boolean;
  }> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/google-sso");

    const body: Record<string, string> = {
      // backend validation requires this exact key name
      id_token: params.idToken,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
      terminal_identification_number: deviceParams.terminalIdentificationNumber,
      deviceType: String(deviceParams.deviceType),
      device_type: String(deviceParams.deviceType),
    };
    if (params.invite_code) {
      body.invite_code = params.invite_code;
    }

    let data: GoogleSsoData;
    try {
      data = await requestEnvelopeData<GoogleSsoData>(path, {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (e) {
      if (e instanceof AppError && e.code === "BAD_REQUEST") {
        try {
          const parsed = JSON.parse(e.meta.responseText ?? "");
          if (parsed?.message === INVITE_CODE_REQUIRED_MESSAGE) {
            throw new AppError(e.message, "BAD_REQUEST", { ...e.meta, inviteCodeRequired: true });
          }
        } catch (inner) {
          if (inner instanceof AppError) throw inner;
        }
      }
      throw e;
    }

    if (!data.email) {
      throw new AppError("Invalid /auth/google-sso response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return {
      email: data.email,
      userId: data.userId,
      secretKey: data.secretKey,
      accessToken: data.accessToken,
      isNewUser: data.isNewUser,
    };
  },

  async invalidateGoogleSso(params?: {
    scope?: "current_session";
    reason?: "user_initialize";
  }): Promise<GoogleSsoInvalidateResult> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/google-sso/invalidate");

    const data = await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          scope: params?.scope ?? "current_session",
          terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
          reason: params?.reason ?? "user_initialize",
        }),
      });
    });

    return {
      invalidated: Boolean(data?.invalidated ?? data?.alreadyInvalidated ?? true),
      alreadyInvalidated: data?.alreadyInvalidated === true ? true : undefined,
      effectiveFrom: typeof data?.effectiveFrom === "string" ? data.effectiveFrom : null,
    };
  },
};
